import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from '../common/reducers/utilities';
import { inside } from '../common/utils/geoUtilities';
import { ensureArray } from '../common/utils/arrayUtilities';

const comparemapZones = (l1, l2) => l1.name === l2.name;

const mapZoneSlice = createSlice({
    name: 'mapZones',
    initialState: {
        data: []
    },
    reducers: {
        set: (state, action) => {
            state.data = action.payload
        },
        addOrUpdate: (state, action) => {
            for (const z of ensureArray(action.payload)) {
                let idx = state.data.findIndex(z2 => comparemapZones(z, z2));
                if (idx >= 0) {
                    state.data.splice(idx, 1, z);
                } else {
                    state.data.push(z);
                }
            }
        },
        remove: (state, action) => {
            for (const z of ensureArray(action.payload)) {
                let idx = state.data.findIndex(z2 => comparemapZones(z2, z));
                if (idx >= 0) {
                    state.data.splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => mapZoneSlice.getInitialState());
    }
});

// #region network operations
const fetchMapZones = network => network.get('/api/v1/mapzones', mapZoneSlice.actions.set);
export const updateMapZone = (network, delta) => network.put('/api/v1/mapzones', delta, mapZoneSlice.actions.addOrUpdate);
export const createMapZone = (network, zone) => network.post('/api/v1/mapzones', zone, mapZoneSlice.actions.addOrUpdate);
export const removeMapZone = (network, id) => network.delete('/api/v1/mapzone/' + encodeURIComponent(id), mapZoneSlice.actions.remove);
// #endregion

// #region selectors
const selectMapZones = state => state.mapZones.data;

// we now make a reselect to memoize a select function parameterized by jobid and expose that factory through a convenience function
const selectMapZoneByID = createSelector([selectMapZones, (mapZones, id) => id], (mapZones, id) => mapZones.find(z => z.id === id));
const selectMapZone = id => state => selectMapZoneByID(state, id);

const selectMapZonesByLocation = createSelector([selectMapZones, (mapZones, latitude) => latitude, (mapZones, latitude, longitude) => longitude], (mapZones, latitude, longitude) => mapZones.filter(z => inside([longitude, latitude], z.zone)));
const selectMapZonesByPoint = ({ latitude, longitude }) => state => selectMapZonesByLocation(state, latitude, longitude);
// #endregion

// #region listeners
const listen = {
    mapZoneCreated: (mapZone, { dispatch }) => dispatch(mapZoneSlice.actions.addOrUpdate(mapZone)),
    mapZoneUpdated: (mapZone, { dispatch }) => dispatch(mapZoneSlice.actions.addOrUpdate(mapZone)),
    mapZoneRemoved: (mapZone, { dispatch }) => dispatch(mapZoneSlice.actions.remove(mapZone))
}
// #endregion

// #region permission checks
export const canInsertZone = (credentials, zone) => credentials.access.hasFull('administrator');
export const canUpdateZone = canInsertZone;
export const canRemoveZone = canInsertZone;
// #endregion

export const [useMapZones, realizeMapZones] = dataHookFactory(selectMapZones, { run: fetchMapZones, listen });
export const [useMapZone] = dataHookFactory(selectMapZone, initMapFactory(id => fetchMapZones, listen));
export const [useMapZonesForPoint] = dataHookFactory(selectMapZonesByPoint, ({ latitude, longitude }) => ({ run: fetchMapZones, listen}));

export const mapZoneInit = { run: fetchMapZones, listen }

const reducerDetails = { name: mapZoneSlice.name, reducer: mapZoneSlice.reducer };
export default reducerDetails;